import { aiBaseModel, aiModelVariant } from '@seoplum/core'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { type AiMessageRow, useAiRun } from '@/lib/admin-ai'
import { formatCount, formatExact } from '@/lib/format'
import { formatMoneyPrecise } from '@/lib/money'
import { BACK_LINK, DetailHeader, Field, JsonBlock, stamp, Tag } from '../-parts'

export const Route = createFileRoute('/_app/admin/ai/runs/$runId')({
  component: RunPage,
})

/**
 * One call, whole.
 *
 * Nothing streams — the run is the record — so this is the only account of what
 * was asked and what came back. The transcript is stored in full deliberately:
 * the question a model call exists to answer is usually a question about the
 * exact words, and truncating the log answers it for every run nobody has
 * thought to ask about yet.
 */
function RunPage() {
  const { runId } = Route.useParams()
  const { data: run, isPending, error } = useAiRun(runId)

  if (error) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          title="Run not found"
          description="No model run on this deployment carries that id."
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/admin/ai">Back to runs</Link>
            </Button>
          }
        />
      </div>
    )
  }

  if (isPending || !run) return <Skeleton className="h-96 w-full" />

  const variant = aiModelVariant(run.modelId)

  return (
    <>
      <DetailHeader
        back={
          <Link to="/admin/ai" className={BACK_LINK}>
            <ArrowLeftIcon className="size-3" aria-hidden />
            Runs
          </Link>
        }
        title={<span className="font-mono">{run.purpose}</span>}
        meta={
          <>
            <span className="font-mono text-[12px]">{aiBaseModel(run.modelId)}</span>
            {variant && <Tag tone="plain">{variant}</Tag>}
            <Tag tone={run.status === 'failed' ? 'bad' : 'plain'}>{run.status}</Tag>
            {run.upstreamProvider && (
              <span className="font-mono text-[12px] text-muted-foreground">
                {run.upstreamProvider}
              </span>
            )}
            {run.organization && <span className="text-[12px]">{run.organization}</span>}
            {run.host && (
              <span className="font-mono text-[12px] text-muted-foreground">{run.host}</span>
            )}
          </>
        }
      />

      <div className="space-y-4">
        {run.error && (
          <Panel title="Error">
            <p className="font-mono text-[12.5px] text-destructive">{run.error}</p>
          </Panel>
        )}

        <Panel title="Run">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field label="Id" wide>
              {run.id}
            </Field>
            <Field label="Model" wide>
              {run.modelId}
            </Field>
            <Field label="Generation">{run.generationId}</Field>
            <Field label="Finish reason">{run.finishReason}</Field>
            <Field label="Attempts">{run.attempts}</Field>
            <Field label="Cost">{formatMoneyPrecise(run.costUsd)}</Field>
            <Field label="Prompt">
              {run.promptId === null ? null : (
                <Link
                  to="/admin/ai/prompts/$promptId"
                  params={{ promptId: run.promptId }}
                  className="text-link hover:underline"
                >
                  {run.promptKey} v{run.promptVersion ?? '?'}
                </Link>
              )}
            </Field>
            <Field label="Hook">{run.hook}</Field>
            <Field label="Requested by">{run.requestedBy}</Field>
            <Field label="Job">{run.jobId}</Field>
            <Field label="Created">{stamp(run.createdAt)}</Field>
            <Field label="Started">{stamp(run.startedAt)}</Field>
            <Field label="Finished">{stamp(run.finishedAt)}</Field>
          </dl>

          <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-border pt-4 sm:grid-cols-4">
            <Field label="Input tokens">{formatExact(run.tokens.input)}</Field>
            <Field label="Output tokens">{formatExact(run.tokens.output)}</Field>
            <Field label="Cache read">{formatExact(run.tokens.cacheRead)}</Field>
            <Field label="Cache write">{formatExact(run.tokens.cacheWrite)}</Field>
          </dl>
        </Panel>

        <Panel
          title="Transcript"
          meta={`${formatCount(run.messages.length)} turns`}
          flush
          className="min-w-0"
        >
          <ul className="divide-y divide-border">
            {run.messages.map((message) => (
              <Message key={message.seq} message={message} />
            ))}
            {run.messages.length === 0 && (
              <li className="px-4 py-4 text-[13px] text-muted-foreground">
                Nothing was recorded on this run.
              </li>
            )}
          </ul>
        </Panel>

        {/* The same answer parsed, written only once it validated against the
            shape the request declared. Prose above with nothing here is a run
            that answered and answered wrong. */}
        {run.outputJson && (
          <Panel title="Parsed output">
            <JsonBlock value={run.outputJson} />
          </Panel>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          {run.params && (
            <Panel title="Params">
              <JsonBlock value={run.params} />
            </Panel>
          )}
          {run.hookContext && (
            <Panel title="Hook context">
              <JsonBlock value={run.hookContext} />
            </Panel>
          )}
        </div>

        {run.input && (
          <Panel title="Input">
            <p className="max-h-[400px] overflow-auto text-[13px] leading-[1.6] whitespace-pre-wrap">
              {run.input}
            </p>
          </Panel>
        )}
      </div>
    </>
  )
}

function Message({ message }: { message: AiMessageRow }) {
  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex items-baseline gap-2">
        <span className="mono-label text-muted-foreground">{message.role}</span>
        <span className="font-mono text-[11px] text-muted-foreground tabular">{message.seq}</span>
      </div>
      {/* What a model said is prose, so it is set as prose. Everything framing
          it is the machine's voice. */}
      {message.content && (
        <p className="text-[13px] leading-[1.65] whitespace-pre-wrap">{message.content}</p>
      )}
      {message.detail && <JsonBlock value={message.detail} className="max-h-[240px]" />}
    </li>
  )
}
